// Clearance offers, deals & schemes and influencer trial requests
import apiClient from './api'

export type PromoState = 'live' | 'scheduled' | 'ended' | 'paused' | 'sold_out' | 'exhausted'

export interface ClearanceOffer {
  id: string
  product: { id: string; name: string; item_code: string; image?: string | null } | null
  variant_id: string | null
  variant_name: string | null
  discount_type: 'percent' | 'fixed_price'
  discount_value: number
  regular_price: number | null
  clearance_price: number | null
  badge_text: string | null
  notes: string | null
  starts_at: string
  ends_at: string
  is_active: boolean
  state: PromoState
  max_quantity: number | null
  sold_quantity: number
  remaining_quantity: number | null
}

export interface ClearanceInput {
  product_id: string
  variant_id?: string | null
  discount_type: 'percent' | 'fixed_price'
  discount_value: number
  starts_at: string
  ends_at: string
  max_quantity?: number | null
  badge_text?: string | null
  notes?: string | null
}

export type BenefitType = 'cashback_fixed' | 'cashback_percent' | 'gift'

export interface DealTier {
  threshold_type: 'order_value' | 'quantity'
  threshold: number
  benefit: {
    type: BenefitType
    value?: number
    max_amount?: number
    gift_variant_id?: string
    gift_quantity?: number
    gift_name?: string
  }
}

export interface Deal {
  id: string
  name: string
  slug: string
  deal_type: 'seasonal' | 'cashback' | 'free_gift' | 'reseller_scheme'
  season_tag: string | null
  description: string | null
  terms: string | null
  banner_url: string | null
  starts_at: string
  ends_at: string
  is_active: boolean
  show_on_portal: boolean
  priority: number
  stackable: boolean
  audience_type: 'all' | 'customer_types' | 'customer_groups'
  audience_ids: string[]
  scope_type: 'order' | 'products' | 'categories'
  scope_ids: string[]
  scope_items: { id: string; name: string }[]
  tiers: DealTier[]
  repeatable: boolean
  max_redemptions: number | null
  per_customer_limit: number | null
  redemption_count: number
  state: PromoState
  stats?: Record<string, { count: number; amount: number }>
}

export type DealInput = Omit<Deal, 'id' | 'slug' | 'state' | 'redemption_count' | 'scope_items' | 'stats'>

export interface DealRedemptionRow {
  id: string
  customer_name: string
  order_number: string
  benefit_type: string
  amount: number
  description: string | null
  status: string
  gift_quantity: number | null
  created_at: string
  credited_at: string | null
}

export type InfluencerStatus = 'pending' | 'approved' | 'rejected' | 'dispatched' | 'review_received'

export interface InfluencerRequest {
  id: string
  reference_code: string
  name: string
  instagram_handle: string
  profile_url: string | null
  followers_count: number | null
  email: string | null
  phone: string
  requested_products: string | null
  message: string | null
  address_line: string
  city: string
  state: string
  pincode: string
  status: InfluencerStatus
  reviewed_at: string | null
  rejection_reason: string | null
  admin_notes: string | null
  approved_products: string | null
  courier: string | null
  tracking_number: string | null
  dispatched_at: string | null
  review_url: string | null
  review_received_at: string | null
  created_at: string
}

export interface Paged<T> {
  items: T[]
  total: number
  page: number
  page_size: number
}

const base = '/admin/promotions'

export const promotionService = {
  listClearance: async (params: { state?: string; search?: string; page?: number; page_size?: number }) =>
    (await apiClient.get<Paged<ClearanceOffer>>(`${base}/clearance`, { params })).data,
  createClearance: async (data: ClearanceInput) => (await apiClient.post<ClearanceOffer>(`${base}/clearance`, data)).data,
  updateClearance: async (id: string, data: Partial<ClearanceInput> & { is_active?: boolean }) =>
    (await apiClient.put<ClearanceOffer>(`${base}/clearance/${id}`, data)).data,
  stopClearance: async (id: string) => (await apiClient.post<ClearanceOffer>(`${base}/clearance/${id}/stop`)).data,
  deleteClearance: async (id: string) => { await apiClient.delete(`${base}/clearance/${id}`) },

  listDeals: async (params: { state?: string; deal_type?: string; page?: number; page_size?: number }) =>
    (await apiClient.get<Paged<Deal>>(`${base}/deals`, { params })).data,
  getDeal: async (id: string) => (await apiClient.get<Deal>(`${base}/deals/${id}`)).data,
  createDeal: async (data: DealInput) => (await apiClient.post<Deal>(`${base}/deals`, data)).data,
  updateDeal: async (id: string, data: Partial<DealInput>) => (await apiClient.put<Deal>(`${base}/deals/${id}`, data)).data,
  stopDeal: async (id: string) => (await apiClient.post<Deal>(`${base}/deals/${id}/stop`)).data,
  deleteDeal: async (id: string) => { await apiClient.delete(`${base}/deals/${id}`) },
  dealRedemptions: async (id: string, page = 1) =>
    (await apiClient.get<Paged<DealRedemptionRow>>(`${base}/deals/${id}/redemptions`, { params: { page } })).data,
  reverseRedemption: async (id: string) => (await apiClient.post(`${base}/redemptions/${id}/reverse`)).data,
  searchVariants: async (q: string) =>
    (await apiClient.get<{ id: string; name: string; sku: string; product_id: string; product_name: string }[]>(
      `${base}/variant-search`, { params: { q } })).data,

  listInfluencerRequests: async (params: { status?: string; search?: string; page?: number; page_size?: number }) =>
    (await apiClient.get<Paged<InfluencerRequest> & { status_counts: Record<string, number> }>(
      `${base}/influencer-requests`, { params })).data,
  influencerAction: async (id: string, body: Record<string, string | undefined> & { action: string }) =>
    (await apiClient.post<InfluencerRequest>(`${base}/influencer-requests/${id}/action`, body)).data,
}

export const STATE_COLOR: Record<string, 'success' | 'info' | 'default' | 'warning' | 'error'> = {
  live: 'success', scheduled: 'info', ended: 'default', paused: 'warning', sold_out: 'error', exhausted: 'error',
}

/** datetime-local value (browser local time) from an ISO string */
export const toLocalInput = (iso?: string | null): string => {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** ISO string (UTC) from a datetime-local value */
export const fromLocalInput = (value: string): string => new Date(value).toISOString()
